export type ExceptionContext = Readonly<
  Record<string, string | number | boolean | null | undefined>
>

export interface OperationIdentity {
  operation: string
  message?: string
  context?: ExceptionContext
}

export const EXCEPTION_DISPOSITION = {
  FAILED: 'failed',
  EXPECTED: 'expected',
  CANCELLED: 'cancelled',
  RECOVERED: 'recovered',
  RETRYING: 'retrying',
  DELEGATED: 'delegated',
} as const

export type ExceptionPolicy = OperationIdentity &
  (
    | { disposition?: 'failed' }
    | {
        disposition: 'expected'
        isExpectedError: (cause: unknown) => boolean
        reason: string
      }
    | { disposition: 'recovered'; reason: string }
    | {
        disposition: 'retrying'
        attempt: number
        maxAttempts: number
        reason: string
      }
    | { disposition: 'delegated'; owner: string }
  )

export interface ExceptionOutcome {
  disposition: (typeof EXCEPTION_DISPOSITION)[keyof typeof EXCEPTION_DISPOSITION]
  cause: unknown
  operation: string
}

export interface OperationPolicy<T> extends OperationIdentity {
  isExpectedError?: (cause: unknown) => boolean
  recover?: {
    reason: string
    run: (cause: unknown) => T | Promise<T>
  }
  cleanup?: () => void | Promise<void>
}

export const OPERATION_STATUS = {
  SUCCEEDED: 'succeeded',
  RECOVERED: 'recovered',
  EXPECTED: 'expected',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
} as const

export type OperationOutcome<T> =
  | { status: 'succeeded'; value: T; cleanupCause?: unknown }
  | { status: 'recovered'; value: T; cause: unknown; cleanupCause?: unknown }
  | {
      status: 'expected' | 'cancelled' | 'failed'
      cause: unknown
      originalCause?: unknown
      cleanupCause?: unknown
    }
